import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewChild } from '@angular/core';
import { finalize } from 'rxjs';
import { ApiErrorResponse } from '../../../../core/classes/ApiErrorResponse';
import { BaseModalComponent } from '../../../../core/layout/components/abstracts/base-modal.component';
import { ContentBoxComponent } from '../../../../shared/components/content-box/content-box.component';
import {
  ContentEditableInputComponent
} from '../../../../shared/components/content-editable-input/content-editable-input.component';
import { KeyboardComponent } from '../../../../shared/components/keyboard/keyboard.component';
import { ModalComponent } from '../../../../shared/components/modal/modal.component';
import { TranslatePipe } from '../../../../shared/pipes/trans.pipe';
import { CartItemTypes } from '../../../cart/core/enums/CartItemTypes';
import { CartService } from '../../../cart/core/services/CartService';
import { IKioskProduct } from '../../core/interfaces/IKioskProduct';

@Component({
  selector: 'app-product-price-picker-modal',
  standalone: true,
  imports: [
    ModalComponent,
    TranslatePipe,
    ContentBoxComponent,
    ContentEditableInputComponent,
    KeyboardComponent
  ],
  templateUrl: './product-price-picker-modal.component.html',
  styleUrl: './product-price-picker-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductPricePickerModalComponent extends BaseModalComponent<IKioskProduct> {

  @ViewChild(ContentEditableInputComponent) public contentEditableInputComponent: ContentEditableInputComponent;

  private readonly _cartService = inject(CartService);

  public readonly amountRaw = signal('');
  public readonly isSubmitting = signal(false);
  public readonly isConfirmDisabled = computed(() => this.isSubmitting() || Number(this.amountRaw()) < 1);

  public override open(val?: IKioskProduct): void {
    this.amountRaw.set('');
    this.isSubmitting.set(false);
    super.open(val);
    this.updateAmountValue('');
  }

  public handleKeyboardClear(): void {
    this.updateAmountValue('');
  }

  public handleKeyboardDeleteBack(): void {
    const current = this.amountRaw();
    this.updateAmountValue(current.substring(0, current.length - 1));
  }

  public handleKeyboardKeyClick(val: string): void {
    const current = this.amountRaw();
    this.updateAmountValue(current ? `${current}${val}` : val);
  }

  public addProductToCart(btn: HTMLButtonElement): void {
    if (this.isConfirmDisabled() || !this.data) {
      return;
    }

    this.isSubmitting.set(true);
    btn.classList.add('disabled');

    this._cartService.addConcessionItemToCart({
      itemId: this.data.id,
      itemType: CartItemTypes.Product,
      quantity: 1,
      price: this.contentEditableInputComponent.value.fromCurrency()
    }, { variablePrice: true })
      .pipe(
        finalize(() => {
          this.isSubmitting.set(false);
          btn.classList.remove('disabled');
        })
      )
      .subscribe({
        next: () => this.close(),
        error: ({ error }: ApiErrorResponse) => {
          this.notificationService.pushError(
            'Fehler'.trans('notifications/error.Title'),
            error.message.trans(`add-product-item-to-cart/notifications/${error.message}.Message`)
          );
        }
      });
  }

  private updateAmountValue(val: string): void {
    this.amountRaw.set(val);

    if (this.contentEditableInputComponent) {
      this.contentEditableInputComponent.value = val;
    }
  }
}
